import Head from "next/head";
import { useRouter } from "next/router";
import { buildCanonicalUrl } from "@/utils/canonical";
const defaultSeoData = {
  ar: {
    title: "المنيع | جولات، الكترونيات، أجهزة منزلية",
    description:
      "تسوق الإلكترونيات، الهواتف الذكية، الأجهزة المنزلية، المزيد من أكثر من 20 علامة تجارية عالمية في المملكة العربية السعودية. احصل على توصيل مجاني للطلبات التي تزيد عن 299 ريال سعودي.",
  },
  en: {
    title: "Almanea | Smartphones, Electronics, Appliances, and More",
    description:
      " Shop electronics, smartphones, appliances, and more from more than 20 international brands in Saudi Arabia. Get free deliver on orders above 299 SAR.",
  },
};

// NEXT_PUBLIC_DOMAIN carries no scheme on staging (e.g. "staging3-almanea.net").
// Prepend https:// only when a scheme is missing so envs that already include
// one (or localhost during dev) are left untouched.
const withProtocol = (value) => {
  if (!value) return "";
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
};

// Resolve any OG image to an absolute URL: pass through full URLs, prefix the
// site domain for app-relative ("/logo.webp") assets.
const toAbsoluteImage = (img) => {
  if (!img) return "";
  if (/^https?:\/\//i.test(img)) return img;
  if (img.startsWith("/")) {
    return `${withProtocol(process.env.NEXT_PUBLIC_DOMAIN)}${img}`;
  }
  return img;
};

// Append the CDN auto-resize param without corrupting an existing query string.
const withResize = (url) =>
  url ? `${url}${url.includes("?") ? "&" : "?"}width=600` : "";

// Magento's product "new" attribute can arrive as a boolean, 0/1, or a yes/no
// string. Open Graph's product:condition only accepts new/refurbished/used, so
// coerce to a valid enum value (default "used" when explicitly not new) and omit
// the tag entirely for unrecognized input rather than emit an invalid value.
const CONDITION_VALUES = ["new", "refurbished", "used"];
const normalizeCondition = (value) => {
  if (value === undefined || value === null || value === "") return "";
  const v = String(value).trim().toLowerCase();
  if (CONDITION_VALUES.includes(v)) return v;
  if (["1", "true", "yes"].includes(v)) return "new";
  if (["0", "false", "no", "old"].includes(v)) return "used";
  return "";
};

const SEO = ({
  title,
  description,
  keywords,
  image, // catalog-relative product image (PDP) — kept for backward compat
  imageUrl, // pre-resolved image URL (homepage banner, category logo)
  pathname,
  ogType = "website",
  condition, // product:condition source (PDP: product "new" attribute)
  brand, // product:brand (PDP)
  availability, // product:availability (PDP) — e.g. "in stock"
  priceAmount, // product:price:amount (PDP)
  priceCurrency, // product:price:currency (PDP) — e.g. "SAR"
  isPLP = false,
  children,
}) => {
  const { locale, asPath } = useRouter();
  const siteUrl = process.env.NEXT_PUBLIC_IMAGE_BASEURL || "/";

  const cleanPath = (pathname || asPath || "").split("?")[0].split("#")[0];

  const pageTitle = isPLP ? title : title || defaultSeoData[locale]?.title;
  const pageDescription = isPLP
    ? description
    : description || defaultSeoData[locale]?.description;

  const baseImage = imageUrl
    ? toAbsoluteImage(imageUrl)
    : image
    ? `${siteUrl}/media/catalog/product${image}`
    : "";
  const pageImage = withResize(baseImage);
  const pageUrl = buildCanonicalUrl(cleanPath, locale);
  const productCondition = normalizeCondition(condition);

  return (
    <Head>
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={keywords || ""} />

      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      {pageImage && <meta property="og:image" content={pageImage} />}

      {brand && <meta property="product:brand" content={String(brand)} />}
      {availability && (
        <meta property="product:availability" content={String(availability)} />
      )}
      {productCondition && (
        <meta property="product:condition" content={productCondition} />
      )}
      {priceAmount && (
        <meta property="product:price:amount" content={String(priceAmount)} />
      )}
      {priceCurrency && (
        <meta property="product:price:currency" content={String(priceCurrency)} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      {pageImage && <meta name="twitter:image" content={pageImage} />}
      {children}
    </Head>
  );
};

export default SEO;
